/**
 * Identifiers (docs/d1-schema.md §2).
 *
 * Every row Ticketing mints gets a UUIDv7: a 48-bit big-endian millisecond
 * timestamp followed by randomness, so ids sort by creation time and B-tree
 * inserts stay roughly ordered instead of scattering like v4.
 */

const HEX: string[] = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, '0'));

/** RFC 9562 UUIDv7. `now` is injectable so tests can pin the time prefix. */
export function uuidv7(now: number = Date.now()): string {
  if (!Number.isSafeInteger(now) || now < 0) {
    throw new RangeError(`uuidv7 timestamp must be a non-negative safe integer, got ${now}`);
  }

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  // 48-bit timestamp, most significant byte first.
  bytes[0] = Math.floor(now / 2 ** 40) & 0xff;
  bytes[1] = Math.floor(now / 2 ** 32) & 0xff;
  bytes[2] = Math.floor(now / 2 ** 24) & 0xff;
  bytes[3] = Math.floor(now / 2 ** 16) & 0xff;
  bytes[4] = Math.floor(now / 2 ** 8) & 0xff;
  bytes[5] = now & 0xff;

  // Version 7 in the high nibble of byte 6; RFC 4122 variant in byte 8.
  bytes[6] = 0x70 | (bytes[6]! & 0x0f);
  bytes[8] = 0x80 | (bytes[8]! & 0x3f);

  const hex = (index: number) => HEX[bytes[index]!]!;
  return (
    hex(0) + hex(1) + hex(2) + hex(3) + '-' +
    hex(4) + hex(5) + '-' +
    hex(6) + hex(7) + '-' +
    hex(8) + hex(9) + '-' +
    hex(10) + hex(11) + hex(12) + hex(13) + hex(14) + hex(15)
  );
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export function isUuid(value: string): boolean {
  return UUID_PATTERN.test(value);
}

/** The millisecond timestamp encoded in a UUIDv7, for debugging and tests. */
export function uuidv7Timestamp(value: string): number {
  if (!isUuid(value) || value[14] !== '7') {
    throw new RangeError(`not a UUIDv7: ${value}`);
  }
  return Number.parseInt(value.slice(0, 8) + value.slice(9, 13), 16);
}
